// frontend/src/components/Products/ProductDetails.jsx

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { addToWishlist, removeFromWishlist } from "../../redux/actions/wishlist";
import { addTocart } from "../../redux/actions/cart";
import { toast } from "react-toastify";
import styles from "../../styles/styles";
import { Helmet } from "react-helmet";
import { getCoordinatesFromPin } from "../../utils/pincode";
import { calculateDistance, getMaxServiceableDistance } from "../../utils/distance";

const ProductDetails = React.memo(() => {
  const { id } = useParams(); 
  const dispatch = useDispatch();

  // Initialize product data state
  const [data, setData] = useState(null); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null); 

  // Wishlist and Cart from Redux
  const { wishlist } = useSelector((state) => state.wishlist);
  const { cart } = useSelector((state) => state.cart);
  const { pinCode, coordinates: userCoordinates } = useSelector((state) => state.user);

  // Local States
  const [click, setClick] = useState(false);
  const [select, setSelect] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [totalOrderPriceWithCommission, setTotalOrderPriceWithCommission] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [distanceCategory, setDistanceCategory] = useState(null);
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [errorDistance, setErrorDistance] = useState(null);
  const [isServiceable, setIsServiceable] = useState(true);
  // distance is tracked by effects; keep state in use for serviceability checks
  const [, setDistance] = useState(null);
  const [, setTotalOrderPriceWithoutCommission] = useState(0);
  const [, setDiscountPriceWithCommission] = useState(0);
  const [, setDiscountPrice] = useState(0);
  // Enquiry Modal State
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryDetails, setEnquiryDetails] = useState({ quantity: '', deliveryLocation: '', message: '' });
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const { user } = useSelector((state) => state.user);

  // Send Enquiry Handler
  const handleSendEnquiry = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please login to send an enquiry.");
      return;
    }
    setEnquiryLoading(true);
    setEnquiryDetails({ quantity: '', deliveryLocation: '', message: '' }); // Clear form immediately
    try {
      // Use canonical product path for enquiry link to avoid passing query params or non-canonical hosts
      const canonicalProductLink = `${window.location.protocol}//${window.location.host}/product/${id}`;
      await axios.post(`${server}/user/enquiry/send`, {
        productLink: canonicalProductLink,
        userDetails: {
          name: user.name,
          email: user.email,
          phone: user.phoneNumber || '',
        },
        enquiryDetails,
      });
      setEnquiryOpen(false);
      toast.success("Enquiry sent successfully!");
    } catch (err) {
      toast.error("Failed to send enquiry.");
    } finally {
      setEnquiryLoading(false);
    }
  };
  // Helper function to get combination key
  const getCombinationKey = useCallback(() => {
    return Object.entries(selectedVariants)
      .map(([variantType, value]) => `${variantType}:${value}`)
      .join("|");
  }, [selectedVariants]);

  // Get Variant Stock
  const getVariantStock = useCallback(() => {
    if (!data) return 0;

    if (!data.hasVariants) {
      if (data.stockAvailability === "All time stock available") {
        return Infinity;
      } else if (data.stockAvailability === "Limited stock available") {
        return data.stock;
      } else {
        return 0;
      }
    }

    const combinationKey = getCombinationKey();
    const variant = data.variantStock.find((v) => v.combination === combinationKey);

    if (variant) {
      if (variant.availability === "All time stock available") {
        return Infinity;
      } else if (variant.availability === "Limited stock available") {
        return variant.stock;
      } else {
        return 0;
      }
    }

    return 0;
  }, [data, getCombinationKey]);

  // Fetch Product Data
  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${server}/product/product/${id}`);
        setData(res.data.product);
        setLoading(false);

        // Initialize selected variants if available
        if (res.data.product.hasVariants && res.data.product.variantTypes.length > 0) {
          const initialVariants = {};
          res.data.product.variantTypes.forEach((variantType) => {
            const firstVariant = res.data.product.variantItems[variantType][0];
            initialVariants[variantType] = firstVariant;
          });
          setSelectedVariants(initialVariants);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        setError("Product not found");
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Distance calculation effect
  useEffect(() => {
    const fetchDistance = async () => {
      setDistanceCategory(null);
      setErrorDistance(null);
      setLoadingDistance(true);

      if (!pinCode || !userCoordinates) {
        setErrorDistance("Set pin code to get distance");
        setLoadingDistance(false);
        return;
      }

      if (!data?.shop?.zipCode) {
        setErrorDistance("Seller pin code not available");
        setLoadingDistance(false);
        return;
      }

      try {
        const sellerPinCode = data.shop.zipCode;
        const sellerCoordinates = await getCoordinatesFromPin(sellerPinCode);

        const distance = calculateDistance(
          userCoordinates.lat,
          userCoordinates.lon,
          sellerCoordinates.lat,
          sellerCoordinates.lon
        );

        let category = "N/A";

        if (distance <= 10) {
          category = "Within 10Km / Quick Delivery";
        } else if (distance > 10 && distance <= 50) {
          category = "Within 10-50km / Normal Delivery";
        } else if (distance > 50 && distance <= 100) {
          category = "Within 50-100km / 2 Day Delivery";
        } else if (distance > 100 && distance <= 1000) {
          category = "Within 100-1000km / 3 Day Delivery";
        } else {
          category = "Above 1000km / 1 Week Delivery";
        }

        setDistanceCategory(category);
      } catch (error) {
        console.error("Error calculating distance:", error);
        setErrorDistance("Error calculating distance");
      } finally {
        setLoadingDistance(false);
      }
    };

    if (data && data.shop) {
      fetchDistance();
    }
  }, [pinCode, userCoordinates, data]);

  useEffect(() => {
    const fetchDistance = async () => {
      if (!pinCode || !userCoordinates || !data?.shop?.zipCode) {
        setIsServiceable(false);
        return;
      }

      try {
        const sellerCoordinates = await getCoordinatesFromPin(data.shop.zipCode);
        const calculatedDistance = calculateDistance(
          userCoordinates.lat,
          userCoordinates.lon,
          sellerCoordinates.lat,
          sellerCoordinates.lon
        );
        setDistance(calculatedDistance);

        const maxDistance = getMaxServiceableDistance(data.serviceableLocation);
        setIsServiceable(maxDistance === Infinity || calculatedDistance <= maxDistance);
      } catch (error) {
        console.error("Distance calculation error:", error);
        setIsServiceable(false);
      }
    };

    if (data && data.shop) {
      fetchDistance();
    }
  }, [pinCode, userCoordinates, data]);

  // Pricing Calculations
  const getPriceFromQuantity = useCallback((qty) => {
    const slab = data?.quantitySlabsWithCommission.find(s => qty >= s.min && qty <= s.max);
    const maxSlab = data?.quantitySlabsWithCommission.reduce((max, slab) => (slab.max > max.max ? slab : max), data.quantitySlabsWithCommission[0]);
    const price = slab ? slab.totalPriceWithCommission : maxSlab.totalPriceWithCommission;
    return price;
  }, [data]);

  const getPriceFromQuantityWithoutCommission = useCallback((qty) => {
    const slab = data?.quantitySlabs.find(s => qty >= s.min && qty <= s.max);
    const maxSlab = data?.quantitySlabs.reduce((max, slab) => (slab.max > max.max ? slab : max), data.quantitySlabs[0]);
    const price = slab ? slab.totalPrice : maxSlab.totalPrice;
    return price;
  }, [data]);

  const getDiscountPriceWithCommission = useCallback((qty) => {
    const slab = data?.quantitySlabsWithCommission.find(s => qty >= s.min && qty <= s.max);
    const maxSlab = data?.quantitySlabsWithCommission.reduce((max, slab) => (slab.max > max.max ? slab : max), data.quantitySlabsWithCommission[0]);
    const discountPrice = slab ? slab.discountPriceWithCommission : maxSlab.discountPriceWithCommission;
    return discountPrice;
  }, [data]);
  
  const getDiscountPrice = useCallback((qty) => {
    const slab = data?.quantitySlabs.find(s => qty >= s.min && qty <= s.max);
    const maxSlab = data?.quantitySlabs.reduce((max, slab) => (slab.max > max.max ? slab : max), data.quantitySlabs[0]);
    const discountPrice = slab ? slab.discountPrice : maxSlab.discountPrice;
    return discountPrice; 
  }, [data]);

  // Handle Quantity Change
  const handleQuantityChange = useCallback((newQuantity) => {
    if (!data || !data.quantitySlabsWithCommission) return;
    const validQuantity = Math.max(data.minimumOrderQuantity, newQuantity);

    const availableStock = getVariantStock();
    const finalQuantity =
      availableStock !== Infinity
        ? Math.min(validQuantity, availableStock)
        : validQuantity;
  
    setQuantity(finalQuantity);

    const priceWithCommission = getPriceFromQuantity(validQuantity);
    const priceWithoutCommission = getPriceFromQuantityWithoutCommission(validQuantity);

    const discountPriceWithCommissionVal = getDiscountPriceWithCommission(validQuantity);
    const discountPriceVal = getDiscountPrice(validQuantity);

    setTotalOrderPriceWithCommission(priceWithCommission * validQuantity);
    setTotalOrderPriceWithoutCommission(priceWithoutCommission * validQuantity);

    // Store discount prices in the state
    setDiscountPriceWithCommission(discountPriceWithCommissionVal * validQuantity);
    setDiscountPrice(discountPriceVal * validQuantity);
  }, [data, getVariantStock, getPriceFromQuantity, getPriceFromQuantityWithoutCommission, getDiscountPriceWithCommission, getDiscountPrice]);

  // Initialize Wishlist Click State and Pricing
  useEffect(() => {
    if (wishlist && data && wishlist.find((i) => i._id === data._id)) {
      setClick(true);
    } else {
      setClick(false);
    }

    if (data && data.quantitySlabs) {
      handleQuantityChange(quantity);
    }
  }, [data, wishlist, handleQuantityChange, quantity]);

  // Wishlist Handlers
  const removeFromWishlistHandler = useCallback((product) => {
    setClick((prev) => !prev);
    dispatch(removeFromWishlist(product));
    toast.info("Removed from wishlist");
  }, [dispatch]);

  const addToWishlistHandler = useCallback((product) => {
    setClick((prev) => !prev);
    dispatch(addToWishlist(product));
    toast.success("Added to wishlist");
  }, [dispatch]);

  // Add to Cart Handler
  const addToCartHandler = useCallback(() => {
    if (!pinCode) {
      toast.error("Please set your pincode first to check availability!");
      return;
    }
    if (!isServiceable) {
      toast.error("This product isn't serviceable in your area!");
      return;
    }

    if (!data || !data._id) return;
    if (quantity < data.minimumOrderQuantity) {
      toast.error(`Please order at least ${data.minimumOrderQuantity} ${data.pricePerQty}`);
      return;
    }
    const combinationKey = getCombinationKey();
    const isItemExists = cart && cart.find((i) => i._id === data._id && i.variantKey === combinationKey);
    if (isItemExists) {
      toast.error("Item already in cart!");
    } else {
      const availableStock = getVariantStock();
      if (availableStock < quantity) {
        toast.error("Product stock limited!");
      } else {
        const totalOrderPriceWithCommissionVal = getPriceFromQuantity(quantity);
        const totalOrderPriceWithoutCommissionVal = getPriceFromQuantityWithoutCommission(quantity);
        const discountPriceWithCommissionVal = getDiscountPriceWithCommission(quantity);
        const discountPriceVal = getDiscountPrice(quantity);

        const cartData = {
          ...data,
          qty: quantity,
          totalPriceWithCommission: totalOrderPriceWithCommissionVal,
          totalPriceWithoutCommission: totalOrderPriceWithoutCommissionVal,
          discountPriceWithCommission: discountPriceWithCommissionVal,
          discountPrice: discountPriceVal,
          ...(data.hasVariants && {
            variant: selectedVariants,
            variantKey: combinationKey, // Unique key for cart items
          })
        };

        dispatch(addTocart(cartData));
        toast.success("Item added to cart successfully!");
      }
    }
  }, [data, cart, getVariantStock, quantity, getPriceFromQuantity, getPriceFromQuantityWithoutCommission, getDiscountPriceWithCommission, getDiscountPrice, selectedVariants, dispatch, getCombinationKey, isServiceable, pinCode]);

  // Average rating is computed inside structuredData and breadcrumb where necessary.

  // Always provide robust structured data for SEO
  const structuredData = useMemo(() => {
  if (!data) return {};
  
  const baseStructuredData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": data.name || "Product",
    "image": (data.images && data.images.length > 0) ? data.images.map((img) => img.url) : ["https://www.hawler.co.in/default-placeholder.png"],
    "description": data.description || "Best price and quality product from Hawler.",
    "sku": data._id,
    "brand": {
      "@type": "Brand",
      "name": data.tags || "Hawler",
    },
    "offers": {
      "@type": "Offer",
      "url": `https://www.hawler.co.in/product/${data._id}`,
      "priceCurrency": "INR",
      "price": (data.quantitySlabsWithCommission && data.quantitySlabsWithCommission.length > 0)
        ? Math.min(...data.quantitySlabsWithCommission.map((slab) => slab.totalPriceWithCommission)).toFixed(2)
        : "0.00",
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "availability": (typeof data.stock === "number" && data.stock > 0)
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Hawler",
        "url": "https://www.hawler.co.in",
      },
    },
    "mpn": data._id ? data._id.toString() : "",
    "category": data.category || "General",
  };

  // ONLY add aggregateRating if real reviews exist
  if (data.reviews && data.reviews.length > 0) {
    const totalReviews = data.reviews.length;
    const totalRatings = data.reviews.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = (totalRatings / totalReviews).toFixed(2);
    
    baseStructuredData.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": totalReviews,
    };
  }

  return baseStructuredData;
}, [data]);

  // Breadcrumb Data for SEO
  const breadcrumbData = useMemo(() => {
    if (!data) return {};

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.hawler.co.in",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Products",
          item: "https://www.hawler.co.in/products",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: data.name,
          item: `https://www.hawler.co.in/product/${data._id}`,
        },
      ],
    };
  }, [data]);

  if (loading) {
  return (
    <>
      <Helmet>
          <title>Loading Product | Hawler</title>
          <meta name="description" content="Loading product information from Hawler marketplace." />
          {/* Removed global noindex during loading to avoid bots indexing the temporary state. Server and SSR handle canonical and 404s. */}
        </Helmet>
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-green-600" aria-label="Loading"></div>
      </div>
    </>
  );
}


 if (error) {
  return (
    <>
      <Helmet>
        <title>Product Not Found | Hawler</title>
        <meta name="description" content="The requested product could not be found." />
        {/* NOINDEX REMOVED - Server will handle 404 status */}
      </Helmet>
      <div className="flex items-center justify-center h-screen px-4">
        <p className="text-red-500 text-xl" role="alert">{error}</p>
      </div>
    </>
  ); 
}


  return (
    <div className="bg-white">
      {data && (
        <>
          {/* SEO Meta Tags */}
          <Helmet>
            <title>{data.name ? `${data.name} | Hawler` : 'Product | Hawler'}</title>
            <meta name="description" content={data.description || 'Best price and quality product from Hawler.'} />
            <meta name="keywords" content={data.tags ? `${data.tags}, ${data.category}` : data.category || 'Hawler, Products'} />
            {/* CANONICAL REMOVED - Server middleware handles it */}
            <meta property="og:title" content={data.name || 'Product'} />
            <meta property="og:description" content={data.description || 'Best price and quality product from Hawler.'} />
            <meta property="og:image" content={data.images && data.images[0]?.url ? data.images[0].url : 'https://www.hawler.co.in/default-placeholder.png'} />
            <meta property="og:url" content={`https://www.hawler.co.in/product/${data._id}`} />
            <meta property="og:type" content="product" />
            <script type="application/ld+json">
              {JSON.stringify(structuredData)}
            </script>
            <script type="application/ld+json">
              {JSON.stringify(breadcrumbData)}
            </script>
          </Helmet>

          <main className={`${styles.section} w-[90%] 800px:w-[80%] mx-auto py-5`}>
            <article className="w-full">
              <div className="flex flex-col 800px:flex-row">
                {/* Product Images */}
                <section className="w-full 800px:w-1/2 flex flex-col items-center">
                  {/* Main Image Container */}
                  <div className="max-w-[700px] max-h-[700px] w-full h-auto">
                    <img
                      src={data.images[select]?.url}
                      alt={`${data.name} - ${data.category} from ${data.tags || 'Hawler'} - Buy online`}
                      loading="lazy"
                      className="w-full h-auto max-w-[700px] max-h-[700px] object-contain rounded"
                    />
                  </div>
                  {/* Thumbnails Container */}
                  <div className="w-full flex flex-wrap mt-4 justify-center 700px:justify-start">
                    {data.images.map((img, index) => (
                      <button
                        key={index}
                        className={`cursor-pointer m-1 border ${select === index ? "border-gray-600" : "border-transparent"} rounded`}
                        onClick={() => setSelect(index)}
                        aria-label={`View image ${index + 1}`}
                      >
                        <img
                          src={img.url}
                          alt={`${data.name} ${index + 1}`}
                          loading="lazy"
                          className="w-20 h-20 object-cover rounded"
                        />
                      </button>
                    ))}
                  </div>
                </section>

                {/* Product Details */}
                <section className="w-full 800px:w-1/2 pt-5 800px:pt-0 800px:pl-10">
                  <header>
                    <h1 className={`${styles.productTitle} text-2xl font-semibold`}>{data.name}</h1>
                    <p className="text-gray-600 mt-2">{data.description}</p>
                  </header>

                  {/* Pricing Table */}
                  {data.quantitySlabsWithCommission && data.quantitySlabsWithCommission.length > 0 && (
                    <div className={styles.tableContainer}>
                      <h4 className={`${styles.productDiscountPrice} mt-4`}>Unit Price :</h4>
                      <table className={`${styles.table} w-full mt-2`}>
                        <thead className={styles.tableHeader}>
                          <tr>
                            <th className={styles.tableCell}>Min</th>
                            <th className={styles.tableCell}>Max</th>
                            <th className={styles.tableCell}>List Price per {data.pricePerQty}</th>
                            <th className={styles.tableCell}>Price without GST per {data.pricePerQty}</th>
                            <th className={styles.tableCell}>Price including GST ({data.gstPercentage}%) per {data.pricePerQty}</th>
                          </tr>
                        </thead>
                        <tbody className={styles.tableBody}>
                          {data.quantitySlabsWithCommission.map((slab, index) => (
                            <tr key={index}>
                              <td className={styles.tableCell}>{slab.min}</td>
                              <td className={styles.tableCell}>{slab.max}</td>
                              <td className={styles.tableCell}>Rs {slab.originalPriceWithCommission.toFixed(2)}</td>
                              <td className={styles.tableCell}>Rs {slab.discountPriceWithCommission.toFixed(2)}</td>
                              <td className={styles.tableCell}>Rs {slab.totalPriceWithCommission.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Additional Product Information */}
                  <div className="mt-4">
                    <div className="flex flex-col gap-2">
                      <h4 className={`${styles.productDiscountPrice}`}>
                        Make / Brand: <span className="font-light">{data.tags}</span> 
                      </h4>
                      <h4 className={`${styles.productDiscountPrice}`}>
                        GST: <span className="font-light">{data.gstPercentage}%</span>
                      </h4>
                      <h4 className={`${styles.productDiscountPrice}`}>
                        Shop Delivery Lead Time: <span className="font-light">{data.leadTime}</span>
                      </h4>
                      <h4 className={`${styles.productDiscountPrice}`}>
                        Minimum order Quantity: <span className="font-light">{data.minimumOrderQuantity || 1}</span>
                      </h4>
                      {/* Delivery Distance Category */}
                      <h4 className={`${styles.productDiscountPrice}`}>
                        Transport Lead Time: <span className="font-light">
                          {loadingDistance && "Calculating..."}
                          {!loadingDistance && errorDistance && errorDistance}
                          {!loadingDistance && !errorDistance && distanceCategory}
                        </span>
                      </h4>
                    </div>
                  </div>

                  {/* Variant Selection Buttons */}
                  {data.hasVariants && data.variantTypes.map((variantType) => (
                    <div key={variantType} className="mt-4">
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        {variantType}:
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {data.variantItems[variantType].map((itemValue) => (
                          <button
                            key={itemValue}
                            type="button"
                            className={`px-4 py-2 rounded-full border ${
                              selectedVariants[variantType] === itemValue
                                ? "bg-gray-600 text-white border-gray-400"
                                : "bg-white text-gray-700 border-gray-300"
                            } hover:bg-gray-400 hover:text-white transition`}
                            onClick={() => {
                              setSelectedVariants({
                                ...selectedVariants,
                                [variantType]: itemValue,
                              });
                            }}
                            aria-pressed={selectedVariants[variantType] === itemValue}
                          >
                            {itemValue}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Quantity Selector and Wishlist */}
                  <div className="flex items-center mt-5 justify-between pr-3">
                    {/* Quantity Selector */}
                    <div>
                      <label htmlFor="quantity" className="block text-lg font-medium text-gray-700">
                        Quantity
                      </label>
                      <div className="flex items-center">
                        <button 
                          className="text-2xl px-3 py-2 border rounded-l hover:bg-gray-200"
                          onClick={() => handleQuantityChange(quantity - 1)}
                          disabled={quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          id="quantity"
                          name="quantity"
                          value={quantity}
                          onChange={(e) => handleQuantityChange(parseInt(e.target.value, 10) || data.minimumOrderQuantity)}
                          className="mt-1 block w-24 text-2xl text-center border-t border-b border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-lg"
                          min={data.minimumOrderQuantity}
                          aria-label="Quantity"
                        />
                        <button 
                          className="text-2xl px-3 py-2 border rounded-r hover:bg-gray-200"
                          onClick={() => handleQuantityChange(quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    {/* Wishlist Icon */}
                    <div>
                      {click ? (
                        <AiFillHeart
                          size={30}
                          className="cursor-pointer text-red-500"
                          onClick={() => removeFromWishlistHandler(data)}
                          aria-label="Remove from wishlist"
                        />
                      ) : (
                        <AiOutlineHeart
                          size={30}
                          className="cursor-pointer text-gray-500"
                          onClick={() => addToWishlistHandler(data)}
                          aria-label="Add to wishlist"
                        />
                      )}
                    </div>
                  </div>

                  {/* Total Order Price and Product ID */}
                  <div className="flex items-center mt-5 justify-between pr-3">
                    <h4 className={`${styles.productDiscountPrice}`}>
                      Total Order Price: Rs {totalOrderPriceWithCommission.toFixed(2)}
                    </h4>
                  </div>
                  <div className="flex items-center mt-2 justify-between pr-3">
                    <h4 className={`${styles.productDiscountPrice} text-[#141414]`}>
                      Product ID: <span className="font-light">{data._id}</span>
                    </h4>
                  </div>
                  {(!pinCode || !isServiceable) && (
    <div style={{
      padding: '16px',
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      borderRadius: '8px',
      margin: '16px 0',
      fontSize: '16px',
      textAlign: 'center'
    }}>
      {!pinCode ? (
        "Please set your pincode to check availability"
      ) : (
        <>
          This product isn't serviceable in your area. Equire us to get the product or{" "}
          <a href="https://wa.me/08072368475" style={{color: '#2563eb', textDecoration: 'underline'}}>WhatsApp: +91 8072368475</a> or{" "}
          <a href="mailto:sales@hawler.co.in" style={{color: '#2563eb', textDecoration: 'underline'}}>email: sales@hawler.co.in</a>
        </>
      )}
    </div>
  )}

                  {/* Add to Cart and Send Enquiry Buttons */}
                  <div className="flex items-center mt-5 justify-between pr-3">
                    <button
                      style={{
                        backgroundColor: !pinCode || !isServiceable || getVariantStock() === 0 ? '#d1d5db' : '#000',
                        color: '#fff',
                        fontWeight: 'bold',
                        padding: '8px 16px',
                        borderRadius: '9999px',
                        cursor: !pinCode || !isServiceable || getVariantStock() === 0 ? 'not-allowed' : 'pointer',
                        opacity: !pinCode || !isServiceable || getVariantStock() === 0 ? 0.7 : 1
                      }}
                      onClick={addToCartHandler}
                      disabled={!pinCode || !isServiceable || getVariantStock() === 0}
                    >
                      Add to Cart
                    </button>
                    <button
                      style={{
                        backgroundColor: '#000',
                        color: '#fff',
                        fontWeight: 'bold',
                        padding: '8px 16px',
                        borderRadius: '9999px',
                        marginLeft: '12px',
                      }}
                      onClick={() => setEnquiryOpen(true)}
                    >
                      Send Enquiry
                    </button>
                  </div>
                  {/* Enquiry Modal (no react-modal) */}
                  {enquiryOpen && (
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      background: 'rgba(0,0,0,0.5)',
                      zIndex: 1000,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <div style={{
                        background: '#fff',
                        borderRadius: 8,
                        maxWidth: 400,
                        width: '90%',
                        padding: 24,
                        boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                        position: 'relative',
                      }}>
                        <h2 className="text-xl font-semibold mb-4">Send Enquiry</h2>
                        <form onSubmit={handleSendEnquiry}>
                          <label className="block mb-2">Quantity</label>
                          <input
                            type="number"
                            min="1"
                            required
                            className="w-full mb-3 p-2 border rounded"
                            value={enquiryDetails.quantity}
                            onChange={e => setEnquiryDetails({ ...enquiryDetails, quantity: e.target.value })}
                          />
                          <label className="block mb-2">Delivery Location</label>
                          <input
                            type="text"
                            required
                            className="w-full mb-3 p-2 border rounded"
                            value={enquiryDetails.deliveryLocation}
                            onChange={e => setEnquiryDetails({ ...enquiryDetails, deliveryLocation: e.target.value })}
                          />
                          <label className="block mb-2">Message</label>
                          <textarea
                            className="w-full mb-3 p-2 border rounded"
                            rows="3"
                            required
                            value={enquiryDetails.message}
                            onChange={e => setEnquiryDetails({ ...enquiryDetails, message: e.target.value })}
                          />
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setEnquiryOpen(false)} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-black text-white rounded" disabled={enquiryLoading}>{enquiryLoading ? "Sending..." : "Send"}</button>
                          </div>
                        </form>
                        <button
                          onClick={() => setEnquiryOpen(false)}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 12,
                            background: 'transparent',
                            border: 'none',
                            fontSize: 22,
                            cursor: 'pointer',
                            color: '#888',
                          }}
                          aria-label="Close"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            </article>
          </main>
        </>
      )}
    </div>
  );
});

export default ProductDetails;
